import { useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { CompleteWorkOrder, LogbookKind, type UpdateWorkOrder, type VendorLinkStatus, type WorkOrder, type WorkOrderEvent } from '@syndiqia/maintenance/contracts';
import { errorMessage, useApi, useAuthHeaders } from '../../api';
import { EventList } from '../VendorPage';
import { useRequest } from '../../comms-queries';
import { Alert, Field, Loading, validate, type FieldErrors } from '../../components/ui';
import { useI18n } from '../../i18n';
import { useAssets, useMaintenanceMutation, useVendors, useWorkOrder } from '../../maintenance-queries';
import { useSyndicate } from '../../session';
import { MaintenanceFrame, centsOf, dollars, todayIso } from './MaintenanceNav';
import { InvoicesPanel, QuotesPanel } from './Purchasing';

export function WorkOrderPage() {
  const { t } = useI18n();
  const s = useSyndicate();
  const { workOrderId = '' } = useParams();
  const order = useWorkOrder(workOrderId);
  const m = t.maintenance;

  return (
    <MaintenanceFrame>
      <Link to={`${s.base}/maintenance`} className="back">
        ← {m.nav.workOrders}
      </Link>
      {order.isPending && <Loading label={t.loading} />}
      {order.isError && <Alert>{errorMessage(order.error, t)}</Alert>}
      {order.data && <Detail order={order.data} />}
    </MaintenanceFrame>
  );
}

function Detail({ order: w }: { order: WorkOrder }) {
  const { t, date, money } = useI18n();
  const s = useSyndicate();
  const m = t.maintenance;
  const manages = s.can('maintenance.manage');
  const open = w.status !== 'completed' && w.status !== 'cancelled';
  const assets = useAssets();
  const vendors = useVendors();
  const request = useRequestIf(w.requestId);
  const update = useMaintenanceMutation<UpdateWorkOrder, WorkOrder>((api, base, input) => api(`${base}/work-orders/${w.id}`, { method: 'PATCH', body: input }));
  const [estimate, setEstimate] = useState(dollars(w.estimateCents));
  const vendor = vendors.data?.find((v) => v.id === w.vendorId);

  const saveEstimate = () => {
    const cents = centsOf(estimate);
    if (cents === undefined ? w.estimateCents !== null : !Number.isNaN(cents) && cents !== w.estimateCents) update.mutate({ estimateCents: cents ?? null });
  };
  const mailto = vendor?.email
    ? `mailto:${vendor.email}?subject=${encodeURIComponent(`${s.name} — ${m.number(w.number)} : ${w.title}`)}&body=${encodeURIComponent(w.description)}`
    : null;

  return (
    <>
      <header className="page-header">
        <div>
          <h1>
            {m.number(w.number)} — {w.title}
          </h1>
          <p className="muted">
            <span className={`badge badge-wo-${w.status}`}>{m.statuses[w.status]}</span>
            {w.priority === 'urgent' && <span className="badge badge-urgent">{m.urgent}</span>}
            {request.data && (
              <>
                {' · '}
                <Link to={`${s.base}/requests/${request.data.id}`}>
                  {m.fromRequest(request.data.number)} : {request.data.subject}
                </Link>
              </>
            )}
          </p>
        </div>
      </header>

      {w.description && <p className="message-body panel">{w.description}</p>}

      {manages && open ? (
        <div className="panel form">
          <div className="row">
            <Field label={m.vendor}>
              <select value={w.vendorId ?? ''} onChange={(e) => update.mutate({ vendorId: e.target.value || null })}>
                <option value="">{m.none}</option>
                {vendors.data
                  ?.filter((v) => v.active || v.id === w.vendorId)
                  .map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name} — {m.trades[v.trade]}
                    </option>
                  ))}
              </select>
            </Field>
            <Field label={m.scheduledOn}>
              <input type="date" value={w.scheduledOn ?? ''} onChange={(e) => update.mutate({ scheduledOn: e.target.value || null })} />
            </Field>
          </div>
          <div className="row">
            <Field label={m.asset}>
              <select value={w.assetId ?? ''} onChange={(e) => update.mutate({ assetId: e.target.value || null })}>
                <option value="">{m.none}</option>
                {assets.data?.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={m.estimate}>
              <input inputMode="decimal" value={estimate} onChange={(e) => setEstimate(e.target.value)} onBlur={saveEstimate} placeholder="0,00 $" />
            </Field>
            <Field label={m.priority}>
              <select value={w.priority} onChange={(e) => update.mutate({ priority: e.target.value as WorkOrder['priority'] })}>
                <option value="normal">{m.normal}</option>
                <option value="urgent">{m.urgent}</option>
              </select>
            </Field>
          </div>
          {update.isError && <Alert>{errorMessage(update.error, t)}</Alert>}
          <div className="form-actions">
            {mailto && (
              <a className="button" href={mailto}>
                ✉ {m.emailVendor}
              </a>
            )}
            <button type="button" className="button" disabled={update.isPending} onClick={() => update.mutate({ status: 'cancelled' })}>
              {m.cancelOrder}
            </button>
          </div>
        </div>
      ) : (
        <dl className="facts panel">
          <dt>{m.vendor}</dt>
          <dd>{w.vendorName ?? m.none}</dd>
          <dt>{m.asset}</dt>
          <dd>{w.assetName ?? m.none}</dd>
          <dt>{m.scheduledOn}</dt>
          <dd>{w.scheduledOn ? date(w.scheduledOn) : m.none}</dd>
          <dt>{m.estimate}</dt>
          <dd>{w.estimateCents != null ? money(w.estimateCents) : m.none}</dd>
          {w.completedOn && (
            <>
              <dt>{m.completedOn}</dt>
              <dd>{date(w.completedOn)}</dd>
              <dt>{m.actualCost}</dt>
              <dd>{w.actualCostCents != null ? money(w.actualCostCents) : m.none}</dd>
              <dt>{m.completionNote}</dt>
              <dd>{w.completionNote ?? m.none}</dd>
            </>
          )}
        </dl>
      )}

      <QuotesPanel order={w} manages={manages} />
      <VendorPanel order={w} manages={manages} open={open} />
      <InvoicesPanel order={w} manages={manages} />
      {manages && open && <CompleteForm order={w} />}
      {!open && <Alert tone="info">{m.closed}</Alert>}
    </>
  );
}

function CompleteForm({ order }: { order: WorkOrder }) {
  const { t } = useI18n();
  const m = t.maintenance;
  const [values, setValues] = useState({ completedOn: todayIso(), cost: dollars(order.estimateCents), note: '', logbookKind: 'repair' });
  const [errors, setErrors] = useState<FieldErrors>({});
  const complete = useMaintenanceMutation<CompleteWorkOrder, WorkOrder>((api, base, input) => api(`${base}/work-orders/${order.id}/complete`, { method: 'POST', body: input }));
  const set = (key: keyof typeof values) => (e: { target: { value: string } }) => setValues((v) => ({ ...v, [key]: e.target.value }));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const { cost, ...rest } = values;
    const result = validate(CompleteWorkOrder, { ...rest, actualCostCents: centsOf(cost) });
    setErrors(result.errors ?? {});
    if (result.data) complete.mutate(result.data);
  };

  return (
    <form onSubmit={submit} className="panel form" noValidate>
      <h2>{m.complete}</h2>
      <p className="muted small">{m.completeHint}</p>
      <div className="row">
        <Field label={m.completedOn} error={errors.completedOn}>
          <input type="date" value={values.completedOn} max={todayIso()} onChange={set('completedOn')} />
        </Field>
        <Field label={m.actualCost} hint={t.common.optional} error={errors.actualCostCents}>
          <input inputMode="decimal" value={values.cost} onChange={set('cost')} placeholder="0,00 $" />
        </Field>
        <Field label={m.logbookKind}>
          <select value={values.logbookKind} onChange={set('logbookKind')}>
            {LogbookKind.options
              .filter((k) => k !== 'correction')
              .map((k) => (
                <option key={k} value={k}>
                  {m.logbookKinds[k]}
                </option>
              ))}
          </select>
        </Field>
      </div>
      <Field label={m.completionNote} hint={t.common.optional}>
        <textarea rows={3} value={values.note} onChange={set('note')} />
      </Field>
      {complete.isError && <Alert>{errorMessage(complete.error, t)}</Alert>}
      <div className="form-actions">
        <button type="submit" className="button button-primary" disabled={complete.isPending}>
          {m.complete}
        </button>
      </div>
    </form>
  );
}

/** The member request a work order comes from, when there is one. */
function useRequestIf(requestId: string | null) {
  return useRequest(requestId ?? '', !!requestId);
}

/** The vendor's secure link to this work order, and what was exchanged with them. */
function VendorPanel({ order, manages, open }: { order: WorkOrder; manages: boolean; open: boolean }) {
  const { t, date } = useI18n();
  const s = useSyndicate();
  const api = useApi();
  const authHeaders = useAuthHeaders();
  const v = t.vendor;
  const status = useQuery({ queryKey: [s.tenantId, 'maintenance', 'vendor-link', order.id], queryFn: () => api<VendorLinkStatus>(`${s.api}/maintenance/work-orders/${order.id}/vendor-link`) });
  const events = useQuery({ queryKey: [s.tenantId, 'maintenance', 'events', order.id], queryFn: () => api<WorkOrderEvent[]>(`${s.api}/maintenance/work-orders/${order.id}/events`) });
  const [created, setCreated] = useState<{ url: string; sentTo: string | null } | null>(null);
  const [copied, setCopied] = useState(false);
  const [note, setNote] = useState('');
  const link = useMaintenanceMutation<{ send: boolean }, { url: string; sentTo: string | null }>((a, base, input) => a(`${base}/work-orders/${order.id}/vendor-link`, { method: 'POST', body: input }));
  const revoke = useMaintenanceMutation<void, void>((a, base) => a(`${base}/work-orders/${order.id}/vendor-link`, { method: 'DELETE' }));
  const message = useMaintenanceMutation<string, unknown>((a, base, text) => a(`${base}/work-orders/${order.id}/events`, { method: 'POST', body: { note: text } }));
  const openFile = async (eventId: string) => {
    const tab = window.open('', '_blank');
    const res = await fetch(`${s.api}/maintenance/work-orders/${order.id}/files/${eventId}`, { headers: authHeaders() });
    if (!res.ok) return tab?.close();
    const url = URL.createObjectURL(await res.blob());
    if (tab) tab.location.href = url;
  };
  const active = status.data?.active;
  if (!order.vendorId && !events.data?.length) return manages && open ? <p className="muted small">{v.noVendor}</p> : null;

  return (
    <section className="panel">
      <h2>
        {v.panel}
        {order.vendorName ? ` : ${order.vendorName}` : ''}
      </h2>
      {manages && open && order.vendorId && (
        <>
          <p className="muted small">{v.linkHint}</p>
          <p className="small">
            {active ? (
              <>
                {v.linkActive(date(active.expiresAt.slice(0, 10)))} · <span className="muted">{active.lastUsedAt ? v.linkUsed(date(active.lastUsedAt.slice(0, 10))) : v.linkNever}</span>
              </>
            ) : (
              <span className="muted">{v.noLink}</span>
            )}
          </p>
          {created?.sentTo && <Alert tone="info">{v.sentTo(created.sentTo)}</Alert>}
          {created && !created.sentTo && (
            <div className="inline-field">
              <input readOnly value={created.url} onFocus={(e) => e.target.select()} />
              <button type="button" className="button button-small" onClick={() => void navigator.clipboard?.writeText(created.url).then(() => setCopied(true))}>
                {copied ? v.copied : t.maintenance.copy}
              </button>
            </div>
          )}
          <div className="form-actions">
            {order.vendorEmail && (
              <button type="button" className="button button-primary button-small" disabled={link.isPending} onClick={() => link.mutate({ send: true }, { onSuccess: (r) => (setCreated(r), setCopied(false)) })}>
                ✉ {v.sendLink}
              </button>
            )}
            <button type="button" className="button button-small" disabled={link.isPending} onClick={() => link.mutate({ send: false }, { onSuccess: (r) => (setCreated(r), setCopied(false)) })}>
              {v.copyLink}
            </button>
            {active && (
              <button type="button" className="link-button small" disabled={revoke.isPending} onClick={() => revoke.mutate(undefined, { onSuccess: () => setCreated(null) })}>
                {v.revoke}
              </button>
            )}
          </div>
          {(link.isError || revoke.isError) && <Alert>{errorMessage(link.error ?? revoke.error, t)}</Alert>}
        </>
      )}
      {!!events.data?.length && (
        <>
          <h3>{v.history}</h3>
          <EventList events={events.data} fileUrl={() => null} onOpenFile={openFile} />
        </>
      )}
      {manages && open && order.vendorId && (
        <form
          className="inline-field vendor-note"
          onSubmit={(e) => {
            e.preventDefault();
            if (note.trim()) message.mutate(note.trim(), { onSuccess: () => setNote('') });
          }}
        >
          <input placeholder={v.boardNote} value={note} onChange={(e) => setNote(e.target.value)} />
          <button type="submit" className="button button-small" disabled={message.isPending || !note.trim()}>
            {v.send}
          </button>
        </form>
      )}
    </section>
  );
}
